<template>
  <div class="selling-material-group-view">

    <div v-if="loading" class="container mx-auto px-6 py-16">
      <div class="max-w-4xl mx-auto space-y-8">
        <Skeleton width="40%" height="1.5rem" class="bg-amber-100/50" />
        <Skeleton width="80%" height="4rem" class="bg-amber-100/50" />
        <Skeleton width="100%" height="12rem" class="bg-amber-100/50" />
      </div>
    </div>

    <div v-else-if="group" class="group-detail-wrapper py-12 md:py-20">
      <div class="container mx-auto px-6 md:px-12">
        <div class="max-w-4xl mx-auto">

          <div class="editorial-card bg-white border border-amber-950/20 rounded-none p-6 md:p-12 shadow-xl relative">
            
            <div class="border-b border-amber-950/20 pb-8 mb-8">
              <div class="flex flex-wrap justify-between items-start gap-4 mb-4">
                <span class="section-label">
                  {{ translate('teacher material') }}
                </span>
                
                <span 
                  class="status-tag" 
                  :class="group.status ? 'status-active' : 'status-inactive'"
                >
                  {{ group.status ? translate('active') : translate('inactive') }}
                </span>
              </div>

              <h1 class="f-font-times-new-roman main-title">
                {{ group.title }}
              </h1>

              <p v-if="group.description" class="main-description mt-4">
                {{ group.description }}
              </p>
            </div>

            <div class="price-cta-block p-6 md:p-8 mb-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div class="text-center md:text-left">
                <span class="price-label">
                  {{ translate('investment') }}
                </span>
                <div class="price-value f-font-times-new-roman">
                  {{ formatCurrency(group.price) }}
                </div>
                <span class="price-subtext block text-xs mt-1 opacity-80">
                  Ou em até 12x no cartão
                </span>
              </div>

              <button class="material-primary-button w-full md:w-auto" @click="tryBuying(group)" v-if="!group.isMyMaterial()">
                <i class="pi pi-shopping-bag mr-2"></i>
                {{ translate('buy now') }}
              </button>
              <button class="material-primary-button w-full md:w-auto" @click="goToMaterialPage(group)" v-else>
                <i class="pi pi-list mr-2"></i>
                {{ translate('see material') }}
              </button>
            </div>

            <div v-if="group.benefits && Object.keys(group.benefits).length > 0" class="details-section mb-10">
              <h2 class="f-font-times-new-roman mb-4 benefits-heading">
                {{ translate('what is included', 'ucfirst') }}:
              </h2>

              <ul class="details-list space-y-3">
                <li 
                  v-for="(value, index) in group.benefits" 
                  :key="index" 
                  class="flex items-start gap-3 text-sm md:text-base text-amber-950/90"
                >
                  <span class="detail-bullet text-rose-700 font-bold">✦</span>
                  <span class="leading-relaxed">{{ value }}</span>
                </li>
              </ul>
            </div>

            <div v-if="1 != 1 && group.hasSellingMaterials()" class="materials-section pt-8 border-t border-amber-950/20">
              <div class="materials-heading mb-4">
                {{ translate('included files:', 'ucfirst') }}
              </div>

              <div class="materials-grid space-y-3">
                <div 
                  v-for="material in visibleMaterials" 
                  :key="material.id"
                  class="material-row flex items-start gap-4 p-4 border border-amber-950/10 hover:border-rose-700/30 transition-colors"
                >
                  <div class="material-icon flex-shrink-0" v-if="1 != 1">
                    {{ getFileIcon(material) }}
                  </div>

                  <div class="material-info flex-1">
                    <span class="material-name font-semibold text-amber-950 block">
                      {{ material.name }}
                    </span>
                    <span v-if="material.description" class="material-description text-xs md:text-sm text-stone-500 block mt-0.5">
                      {{ material.description }}
                    </span>
                  </div>
                </div>
              </div>

              <button 
                v-if="group.sellingMaterials.length > limit" 
                class="see-more-materials mt-4"
                @click="isExpanded = !isExpanded"
              >
                {{ 
                  isExpanded 
                    ? translate('show less') 
                    : translate('see all {count} materials', 'ucfirst', { count: group.sellingMaterials.length }) 
                }}
                <i :class="['pi', isExpanded ? 'pi-chevron-up' : 'pi-chevron-down', 'ml-1 text-xs']"></i>
              </button>
            </div>

            <div v-if="1!=1" class="product-empty p-8 text-center border border-dashed border-amber-950/20 text-stone-500 rounded-none">
              {{ translate('content available after release.') }}
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Skeleton from 'primevue/skeleton';
import SellingMaterialService from '@/services/SellingMaterialService';
import { translate } from '@/composables/translate';
import { useStorageStore } from '@/stores/storage';
import urlHandler from '@/composables/urlHandler';
import { useAuthStore } from '@/stores/auth';

const authUser = useAuthStore();
const storageStore = useStorageStore();
const route = useRoute();

const group = ref(null);
const loading = ref(true);
const isExpanded = ref(false);
const limit = ref(4);

const visibleMaterials = computed(() => {
  if (!group.value || !group.value.hasSellingMaterials()) return [];
  return isExpanded.value 
    ? group.value.sellingMaterials 
    : group.value.sellingMaterials.slice(0, limit.value);
});

const getFileIcon = (material) => {
  if (!material) return 'FILE';
  const mime = material.mime_type || '';

  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('image')) return 'IMG';
  if (mime.includes('word') || mime.includes('document')) return 'DOC';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'PPT';
  
  return 'FILE';
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const loadGroupDetail = async () => {
  loading.value = true;
  const groupId = route.params.group_id || route.params.id;
  
  try {
    group.value = await SellingMaterialService.getGroupDetail(groupId, authUser.getUserId);

    if (group.value && typeof group.value.hasSellingMaterials !== 'function') {
      group.value.hasSellingMaterials = function() {
        return Array.isArray(this.sellingMaterials) && this.sellingMaterials.length > 0;
      };
    }

  } catch (err) {
    console.error('Erro ao carregar os detalhes do grupo:', err);
  } finally {
    loading.value = false;
  }
};

async function tryBuying(sellingMaterialGroup = null) {
    storageStore.save('PURCHASE_ITEM', [sellingMaterialGroup]);
    await urlHandler.redirect(`/purchase`);

    return false;
}

function goToMaterialPage(sellingMaterialGroup = null) {
  urlHandler.redirectAndScroll(`/dashboard/teacher_materials`, {selected: sellingMaterialGroup.id});
}

onMounted(() => {
  loadGroupDetail();
});
</script>

<style scoped>
/* Variáveis Herdadas da Landing Page */
.selling-material-group-view {
  --material-red: var(--blog-red, #8b262a);
  --material-old-red: var(--blog-old-red, #5c1d20);
  --material-cream: var(--blog-beige, #fffaf0);
  --material-gold: var(--blog-gold, #d4af37);

  background-color: var(--material-cream);
  min-height: 100vh;
  color: var(--material-old-red);
}

.editorial-card {
  box-shadow: 20px 20px 0 rgba(106, 51, 40, 0.08);
}

.section-label {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--material-red);
}

.main-title {
  font-size: clamp(2.2rem, 4vw, 3.5rem);
  line-height: 0.95;
  color: var(--material-old-red);
}

.main-description {
  color: #604f48;
  font-size: 1.05rem;
  line-height: 1.7;
}

/* Status tag */
.status-tag {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  padding: 4px 12px;
  font-weight: 700;
  border-radius: 999px;
}
.status-active {
  background-color: #e6f4ea;
  color: #137333;
}
.status-inactive {
  background-color: #fce8e6;
  color: #c5221f;
}

/* Price & CTA Block */
.price-cta-block {
  background: var(--material-cream);
  border: 1px solid var(--material-gold);
}

.price-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #888;
}

.price-value {
  font-size: 2.8rem;
  color: var(--material-red);
  line-height: 1;
}

.material-primary-button {
  padding: 16px 32px;
  border-radius: 999px;
  background: var(--material-red);
  color: #fff;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.material-primary-button:hover {
  transform: translateY(-2px);
  background: var(--material-old-red);
}

/* Details Section */
.benefits-heading {
  font-size: 1.5rem;
  color: var(--material-old-red);
}

.detail-bullet {
  color: var(--material-red);
}

/* Materials List */
.materials-heading {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--material-red);
}

.material-icon {
  width: 38px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--material-red);
  color: var(--material-red);
  font-size: 0.6rem;
  font-weight: 700;
  background: #fff;
}

.see-more-materials {
  border: none;
  background: transparent;
  color: var(--material-red);
  cursor: pointer;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 700;
}

.see-more-materials:hover {
  text-decoration: underline;
}
</style>